import { useAutocomplete } from '@hooks/search/useAutocomplete';
import classNames from 'classnames';
import React from 'react';

export interface SearchAutocompleteProps {
  searchTerm: string;
  search: (searchTerm: string) => void;
  className?: string;
  id?: string;
  returnFocus: () => void;
}

// eslint-disable-next-line react/display-name
const SearchAutocomplete = React.forwardRef<HTMLButtonElement, SearchAutocompleteProps>(
  ({ searchTerm, search, className, id, returnFocus }: SearchAutocompleteProps, ref) => {
    const { data } = useAutocomplete(searchTerm);

    if (!data?.hits?.length) {
      return null;
    }

    return (
      <ul
        className={classNames(
          'tw-list-none tw-absolute tw-top-full tw-bg-neutral-600 tw-inset-x-0 tw-rounded-xl tw-px-0 tw-overflow-hidden',
          className,
        )}
        aria-label="Search suggestions"
        id={id}
      >
        {data.hits?.map(({ query }, i) => (
          <li key={query}>
            <button
              type="button"
              onClick={() => search(query)}
              aria-label={`Search for ${query}`}
              className="tw-py-4 tw-px-6 tw-w-full tw-text-left hover:tw-bg-neutral-300 focus:tw-bg-neutral-300"
              ref={i === 0 ? ref : undefined}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  (
                    e.currentTarget.parentElement?.nextElementSibling
                      ?.children?.[0] as HTMLButtonElement
                  )?.focus();
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  (
                    e.currentTarget.parentElement?.previousElementSibling
                      ?.children?.[0] as HTMLButtonElement
                  )?.focus();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  returnFocus();
                }
              }}
            >
              {query
                .split(
                  new RegExp(`(${searchTerm.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')})`, 'g'),
                )
                .map((part, i) => (part === searchTerm ? <b key={part + i}>{part}</b> : part))}
            </button>
          </li>
        ))}
      </ul>
    );
  },
);

export default SearchAutocomplete;
